/**
 * 访客信息自动采集
 *
 * 采集内容：
 *   - 页面 URL / UTM 来源参数 / 引荐来源
 *   - 设备/浏览器信息（含 OS 版本、浏览器版本检测）
 *   - 访客 ID（3 级 fallback：localStorage → cookie → 浏览器指纹）
 */

export interface VisitorInfo {
  visitorId: string;
  pageUrl: string;
  referrer: string;
  utm: {
    source: string;
    medium: string;
    campaign: string;
    term: string;
    content: string;
  };
  /** @deprecated use device instead */
  deviceJson: string;
  /** @deprecated use browser instead */
  browserJson: string;
  device: { os: string; osVersion: string; browser: string; browserVersion: string };
  browser: { name: string; version: string };
  collectedAt: string;
}

const VISITOR_ID_KEY = "onecall_vid";

function djb2(str: string, seed: number): string {
  let hash = seed;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

/** 浏览器指纹 — 两轮 DJB2（不同种子）→ 64 bit */
function computeFingerprint(): string {
  const signals = [
    navigator.userAgent || "",
    navigator.language || "",
    navigator.platform || "",
    (navigator as any).hardwareConcurrency || "",
    (window.screen || {} as any).width || "",
    (window.screen || {} as any).height || "",
    (window.screen || {} as any).colorDepth || "",
    new Date().getTimezoneOffset(),
  ];
  const str = signals.join("|");
  return "vid_" + djb2(str, 5381) + djb2(str, 7733);
}

/** 3 级 fallback: localStorage → cookie → 指纹 */
function getOrCreateVisitorId(): string {
  // 1. localStorage
  try {
    const ls = localStorage.getItem(VISITOR_ID_KEY);
    if (ls) return ls;
  } catch { /* noop */ }

  // 2. cookie
  const cookies = document.cookie.split(";");
  for (const c of cookies) {
    const trimmed = c.trim();
    if (trimmed.startsWith(VISITOR_ID_KEY + "=")) {
      return trimmed.substring(VISITOR_ID_KEY.length + 1);
    }
  }

  // 3. fingerprint
  const fp = computeFingerprint();
  try { localStorage.setItem(VISITOR_ID_KEY, fp); } catch { /* noop */ }
  document.cookie = `${VISITOR_ID_KEY}=${fp}; path=/; max-age=${2 * 365 * 24 * 60 * 60}; SameSite=Lax`;
  return fp;
}

/** 解析 UTM 参数 */
function parseUtmParams(): VisitorInfo["utm"] {
  if (typeof window === "undefined") {
    return { source: "", medium: "", campaign: "", term: "", content: "" };
  }
  const params = new URLSearchParams(window.location.search);
  return {
    source: params.get("utm_source") || "",
    medium: params.get("utm_medium") || "",
    campaign: params.get("utm_campaign") || "",
    term: params.get("utm_term") || "",
    content: params.get("utm_content") || "",
  };
}

/** OS 检测 */
function detectOS(ua: string): { os: string; osVersion: string } {
  if (/Windows NT 10\.0/.test(ua)) return { os: "Windows", osVersion: "10/11" };
  if (/Windows NT 6\.3/.test(ua)) return { os: "Windows", osVersion: "8.1" };
  if (/Windows NT 6\.1/.test(ua)) return { os: "Windows", osVersion: "7" };
  const macMatch = ua.match(/Mac OS X 10[_\.](\d+)/);
  if (macMatch) return { os: "macOS", osVersion: "10." + macMatch[1] };
  if (/Mac OS X/.test(ua)) return { os: "macOS", osVersion: "" };
  const androidMatch = ua.match(/Android\s+([\d.]+)/);
  if (androidMatch) return { os: "Android", osVersion: androidMatch[1] };
  if (/iPhone|iPad/.test(ua)) return { os: "iOS", osVersion: "" };
  if (/Linux/.test(ua)) return { os: "Linux", osVersion: "" };
  return { os: "Unknown", osVersion: "" };
}

/** 浏览器检测 */
function detectBrowser(ua: string): { name: string; version: string } {
  if (/Edg\/([\d.]+)/.test(ua)) return { name: "Edge", version: ua.match(/Edg\/([\d.]+)/)![1] };
  if (/Chrome\/([\d.]+)/.test(ua) && !/Edg\//.test(ua)) return { name: "Chrome", version: ua.match(/Chrome\/([\d.]+)/)![1] };
  if (/Safari\/([\d.]+)/.test(ua) && !/Chrome/.test(ua)) return { name: "Safari", version: ua.match(/Safari\/([\d.]+)/)![1] };
  if (/Firefox\/([\d.]+)/.test(ua)) return { name: "Firefox", version: ua.match(/Firefox\/([\d.]+)/)![1] };
  return { name: "Unknown", version: "" };
}

/** 主采集函数 */
export function collectVisitorInfo(): VisitorInfo {
  const nav = typeof navigator !== "undefined" ? navigator : null;
  const win = typeof window !== "undefined" ? window : null;
  const doc = typeof document !== "undefined" ? document : null;

  const ua = nav?.userAgent || "";
  const os = detectOS(ua);
  const browser = detectBrowser(ua);

  return {
    visitorId: getOrCreateVisitorId(),
    pageUrl: win?.location.href || "",
    referrer: doc?.referrer || "",
    utm: parseUtmParams(),
    deviceJson: JSON.stringify({ os: os.os, osVersion: os.osVersion, browser: browser.name, browserVersion: browser.version }),
    browserJson: JSON.stringify({ name: browser.name, version: browser.version }),
    device: { os: os.os, osVersion: os.osVersion, browser: browser.name, browserVersion: browser.version },
    browser: { name: browser.name, version: browser.version },
    collectedAt: new Date().toISOString(),
  };
}

/** SDK 初始化时自动采集一次 */
export function autoCollect(): VisitorInfo {
  const info = collectVisitorInfo();
  if (typeof window !== "undefined" && (window as any).__ONECALL_DEBUG__) {
    console.log("[OneCallSDK] Visitor info:", info);
  }
  return info;
}
